"use client";

import * as React from "react";
import { ArrowRight, Code, Cpu, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const mascotImage = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Mascot - Thumbs Up"
};
const heroContent = {
  specRail: {
    vi: "Lộ trình Robotics chính thức // Độ tuổi 4 đến 16+",
    en: "Official Robotics pathway // ages 4 to 16+"
  },
  headline: {
    vi: "Lộ trình Robotics từ kỹ sư nhí đến nhà sáng tạo robot",
    en: "The Robotics pathway: from young engineers to robot builders"
  },
  lead: {
    vi: "Lộ trình đào tạo Robotics bài bản trang bị cho học viên năng lực chế tác cơ khí, tư duy thuật toán và kỹ năng lập trình điều khiển tự động qua từng cấp độ phát triển.",
    en: "A structured Robotics pathway empowering young learners to master mechanical design, algorithmic thinking, and autonomous control across progressive stages."
  },
  subLead: {
    vi: "Khởi đầu từ nền tảng Duplo (4–6 tuổi), tiếp nối cùng WhalesBot AI Module 1S (7+ tuổi) và bứt phá tại các khóa luyện thi đấu Enjoy AI, học viên được rèn luyện chuẩn kỹ sư trong môi trường phòng Lab hiện đại.",
    en: "Starting with the Duplo foundation track (ages 4–6), advancing through WhalesBot AI Module 1S (ages 7+), and reaching Enjoy AI competition-prep stages in an authentic engineering lab."
  },
  primaryCta: {
    vi: "Đăng ký tư vấn",
    en: "Book a Consultation"
  },
  secondaryCta: {
    vi: "Khám phá lộ trình Robotics",
    en: "Explore the Robotics Pathway"
  },
  specs: [{
    label: {
      vi: "Số giai đoạn lộ trình",
      en: "Pathway stages"
    },
    value: {
      vi: "3 giai đoạn",
      en: "3 stages"
    }
  }, {
    label: {
      vi: "Học cụ chính",
      en: "Core equipment"
    },
    value: {
      vi: "Duplo & WhalesBot",
      en: "Duplo & WhalesBot"
    }
  }, {
    label: {
      vi: "Nhóm lứa tuổi",
      en: "Age cohorts"
    },
    value: {
      vi: "4 đến 16 tuổi",
      en: "4 to 16 yrs"
    }
  }]
};
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = React.useState<"vi" | "en">("vi");
  React.useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        return stored === "en" ? "en" : "vi";
      } catch {
        return "vi";
      }
    };
    setLang(getStoredLang());
    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      } else {
        setLang(getStoredLang());
      }
    };
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === "roboto_lang") {
        setLang(getStoredLang());
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: shouldReduceMotion ? 0 : 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="dark" className="relative min-h-[640px] lg:min-h-[780px] flex items-center overflow-hidden bg-foreground text-background py-16 sm:py-20 lg:py-24">
      {/* Structural Laboratory Atmospheric Layers */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Deep architectural radial glows */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />

        {/* Blueprint coordinate matrix pattern */}
        <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--color-primary-foreground)/0.035)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--color-primary-foreground)/0.035)_1px,transparent_1px)] bg-[size:36px_36px]" />

        {/* Crisp edge spec dividers */}
        <div className="absolute top-0 left-0 right-0 h-px bg-primary-foreground/10" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-primary-foreground/10" />
      </div>

      {/* Floating Decorative Mascot (Single default mascot, pointer-events-none, safe placement) */}
      <motion.div aria-hidden="true" animate={shouldReduceMotion ? {} : {
      y: [0, -7, 0],
      rotate: [0, 1.5, 0, -1.5, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }} className="absolute top-3 right-4 sm:top-5 sm:right-8 lg:top-7 lg:right-10 xl:right-16 z-10 pointer-events-none select-none">
        <div className="size-16 sm:size-24 md:size-28 lg:size-32 xl:size-36 flex items-center justify-center bg-transparent">
          <img src={mascotImage.src} data-wvc-srcset={mascotImage.srcset} data-wvc-sizes={mascotImage.sizes} alt={mascotImage.alt} loading="lazy" decoding="async" className="h-full w-full object-contain drop-shadow-lg pointer-events-none select-none bg-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 xl:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-center">
          {/* LEFT COLUMN: Authority Content & Controls */}
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="lg:col-span-7 xl:col-span-7 flex flex-col justify-center">
            {/* Precision Spec Rail Motif & Eyebrow */}
            <motion.div variants={itemVariants} className="mb-5 sm:mb-6">
              <div className="flex items-center gap-2.5">
                <span className="size-2 bg-secondary rounded-sm" />
                <div className="h-px w-14 bg-secondary/80" />
                <span className="font-mono text-xs tracking-wider uppercase text-secondary font-semibold">
                  {heroContent.specRail[lang]}
                </span>
              </div>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-primary-foreground leading-[1.1] mb-5 sm:mb-6">
              {heroContent.headline[lang]}
            </motion.h1>

            {/* Supporting Line & Description */}
            <motion.p variants={itemVariants} className="text-base sm:text-lg lg:text-xl text-primary-foreground/90 leading-relaxed font-sans mb-3 sm:mb-4 max-w-2xl">
              {heroContent.lead[lang]}
            </motion.p>

            <motion.p variants={itemVariants} className="text-sm sm:text-base text-primary-foreground/75 leading-relaxed font-sans mb-8 max-w-xl">
              {heroContent.subLead[lang]}
            </motion.p>

            {/* Button Cluster */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 sm:gap-4 mb-10 sm:mb-12">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base px-8 py-6 rounded-lg transition-[background-color,box-shadow]">
                <Link to="/FreeTrialContactPage">
                  {heroContent.primaryCta[lang]}
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/15 bg-transparent font-semibold text-base px-7 py-6 rounded-lg transition-[background-color,border-color]">
                <Link to="/RoboticsLearningPathwayPage">
                  {heroContent.secondaryCta[lang]}
                </Link>
              </Button>
            </motion.div>

            {/* Mobile Visual Band (Specifically designed for small screens) */}
            <div className="lg:hidden mb-10">
              <div className="relative rounded-xl overflow-hidden border border-secondary/35 shadow-lg bg-card/5 p-1.5 backdrop-blur-sm">
                <div className="relative h-56 sm:h-72 w-full rounded-lg overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png-wvc-sizes" alt="Students actively engineering a robotics prototype at a modern laboratory workbench" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/20 to-transparent" />
                  
                  {/* Compact Mobile Skill Badge */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-foreground/80 border border-secondary/40 backdrop-blur-md">
                      <Cpu className="size-3.5 text-secondary" />
                      <span className="font-mono text-[10px] uppercase font-bold text-primary-foreground">
                        {lang === "vi" ? "Chế tác & Robotics" : "Hardware & Robotics"}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-foreground/80 border border-accent/40 backdrop-blur-md">
                      <Code className="size-3.5 text-accent" />
                      <span className="font-mono text-[10px] uppercase font-bold text-primary-foreground">
                        {lang === "vi" ? "Thuật toán" : "Coding"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lab Metrics Bar */}
            <motion.div variants={itemVariants} className="pt-6 border-t border-primary-foreground/15 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl">
              {heroContent.specs.map((spec, i) => <div key={spec.label.en} data-index={i} className="flex flex-col">
                  <span className="font-mono text-xs uppercase tracking-wider text-primary-foreground/60 mb-1">
                    {spec.label[lang]}
                  </span>
                  <span className="font-serif text-lg sm:text-xl font-bold text-primary-foreground flex items-center gap-2">
                    {i === 0 && <span className="p-1 rounded-md bg-secondary/20 text-secondary shrink-0 inline-flex items-center justify-center">
                        <Cpu className="size-4" />
                      </span>}
                    {i === 1 && <span className="p-1 rounded-md bg-accent/20 text-accent shrink-0 inline-flex items-center justify-center">
                        <ShieldCheck className="size-4" />
                      </span>}
                    {spec.value[lang]}
                  </span>
                </div>)}
            </motion.div>
          </motion.div>

          {/* RIGHT COLUMN: Asymmetric Robotics Visual Composition (Desktop / lg+) */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-5 relative">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            x: 24
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: shouldReduceMotion ? 0 : 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative w-full">
              {/* Outer Glow & Technical Spec Rail Frame */}
              <div className="absolute -inset-2 rounded-2xl bg-gradient-to-tr from-secondary/20 via-transparent to-accent/20 blur-md pointer-events-none" />

              {/* Main Visual Showcase Container */}
              <motion.div animate={shouldReduceMotion ? {} : {
              y: [0, -6, 0]
            }} transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut"
            }} className="relative rounded-2xl p-2 bg-gradient-to-b from-primary-foreground/15 to-primary-foreground/5 border border-secondary/40 shadow-2xl backdrop-blur-md">
                {/* Visual Viewport with Blueprint Accents */}
                <div className="relative h-[480px] xl:h-[540px] w-full rounded-xl overflow-hidden bg-foreground/60">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_engineering_robotics_prototype_workbench.png-wvc-sizes" alt="Students actively engineering a robotics prototype at a modern laboratory workbench" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />

                  {/* Engineered Lab Tint Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-foreground/10" />

                  {/* Corner Calibration Marks */}
                  <div className="absolute top-3 left-3 size-3 border-t-2 border-l-2 border-secondary" />
                  <div className="absolute top-3 right-3 size-3 border-t-2 border-r-2 border-secondary" />
                  <div className="absolute bottom-3 left-3 size-3 border-b-2 border-l-2 border-secondary" />
                  <div className="absolute bottom-3 right-3 size-3 border-b-2 border-r-2 border-secondary" />
                </div>

                {/* Layered Technical Decorative Element: Reticle Circle (Anchored Top-Right) */}
                <motion.div animate={shouldReduceMotion ? {} : {
                rotate: 360
              }} transition={{
                duration: 36,
                repeat: Infinity,
                ease: "linear"
              }} className="absolute -top-8 -right-8 pointer-events-none z-20">
                  <svg className="size-28 xl:size-32 text-secondary/45" viewBox="0 0 160 160" fill="none" stroke="currentColor" strokeWidth="1.25">
                    <circle cx="80" cy="80" r="70" strokeDasharray="6 6" />
                    <circle cx="80" cy="80" r="46" />
                    <line x1="80" y1="0" x2="80" y2="18" />
                    <line x1="80" y1="142" x2="80" y2="160" />
                    <line x1="0" y1="80" x2="18" y2="80" />
                    <line x1="142" y1="80" x2="160" y2="80" />
                  </svg>
                </motion.div>

                {/* Layered Technical Decorative Element: Dashed Hexagon (Anchored Mid-Right) */}
                <motion.div animate={shouldReduceMotion ? {} : {
                y: [0, 8, 0]
              }} transition={{
                duration: 6.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5
              }} className="absolute top-1/2 -right-10 pointer-events-none z-10">
                  <svg className="size-24 xl:size-28 text-accent/40" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.25" strokeDasharray="4 4">
                    <polygon points="50,6 90,28 90,72 50,94 10,72 10,28" />
                    <circle cx="50" cy="50" r="3.5" fill="currentColor" />
                  </svg>
                </motion.div>

                {/* Layered Technical Decorative Element: Circuit-Line Connector (Anchored Bottom-Left) */}
                <motion.div animate={shouldReduceMotion ? {} : {
                x: [0, 5, 0]
              }} transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }} className="absolute -bottom-8 -left-6 pointer-events-none z-20">
                  <svg className="w-40 h-16 text-secondary/50" viewBox="0 0 180 80" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M10 20 H65 L95 55 H165" strokeDasharray="3 3" />
                    <circle cx="10" cy="20" r="3" fill="currentColor" />
                    <circle cx="165" cy="55" r="3" fill="currentColor" />
                    <path d="M45 65 H80 L105 35 H145" />
                    <circle cx="45" cy="65" r="2.5" fill="currentColor" />
                  </svg>
                </motion.div>

                {/* Repositioned Skill Badge 1: Algorithms & Coding (Top-Left of visual) */}
                <motion.div animate={shouldReduceMotion ? {} : {
                y: [0, -6, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }} className="absolute -top-5 -left-6 flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-primary/95 border border-secondary/50 shadow-xl backdrop-blur-md z-30">
                  <span className="p-1 rounded-md bg-secondary/25 text-secondary shrink-0 flex items-center justify-center">
                    <Code className="size-4" />
                  </span>
                  <div className="flex flex-col">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-secondary font-bold leading-tight">
                      {lang === "vi" ? "Trọng tâm kỹ năng" : "Skill Focus"}
                    </span>
                    <span className="font-sans text-xs font-semibold text-primary-foreground">
                      {lang === "vi" ? "Thuật toán & Coding" : "Algorithms & Coding"}
                    </span>
                  </div>
                </motion.div>

                {/* Repositioned Skill Badge 2: Hardware & Robotics (Bottom-Right of visual) */}
                <motion.div animate={shouldReduceMotion ? {} : {
                y: [0, 6, 0]
              }} transition={{
                duration: 5.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.7
              }} className="absolute -bottom-5 -right-6 flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-primary/95 border border-accent/50 shadow-xl backdrop-blur-md z-30">
                  <span className="p-1 rounded-md bg-accent/25 text-accent shrink-0 flex items-center justify-center">
                    <Cpu className="size-4" />
                  </span>
                  <div className="flex flex-col">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-accent font-bold leading-tight">
                      {lang === "vi" ? "Kỹ thuật" : "Engineering"}
                    </span>
                    <span className="font-sans text-xs font-semibold text-primary-foreground">
                      {lang === "vi" ? "Chế tác & Robotics" : "Hardware & Robotics"}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}